import { LocationsMapFilter } from 'api/locations-map';
import { IPanicRequestDTO, IUserLocationDTO, LocationCurrentnessStatus } from 'api/swagger/ApiClient';
import { autoinject } from 'aurelia-framework';
import { watch } from 'aurelia-watch-decorator';
import { DialogHelper } from 'framework/aurelia_dialogs/dialog-helper';
import { DateTimeValueConverter } from 'framework/value_converters/date-and-time';
import * as L from 'leaflet';
import { DomEvent } from 'leaflet';
import 'leaflet.markercluster';
import { MobileAppDashboardService } from 'services/mobile-app-dashboard-service';
import { DataMarker, esriApiKey } from 'support/map';
import { ToastFormat, ToastHelper, ToastType } from 'support/toaster';
import { UserLocationRequestStatus } from 'model/business-enums';
import { TaskMutex } from 'framework/script_utilities/task-executor';

@autoinject
export class AdminMobileAppDashboardScreen {
  locationsMapPlaceholder: HTMLElement;
  locationsChartBox: HTMLElement;
  installationsBox: HTMLElement;
  activeUsersBox: HTMLElement;
  trackingUsersBox: HTMLElement;
  panicAlertsBox: HTMLElement;
  usersLocationsBox: HTMLElement;
  filterBox: HTMLElement;
  eventsList: HTMLElement;
  panicRequestId: string | null;
  map: L.Map;
  mapMinZoom = 2;
  mapMaxZoom = 18;
  mapFilterMaxZoomDelta = 4;
  userLocationsMarkers: L.MarkerClusterGroup;
  panicRequestsMarkers: L.MarkerClusterGroup;
  markersMap: Map<string, DataMarker<IUserLocationDTO>> = new Map();
  filter: LocationsMapFilter = { user: null };
  sharedIconOptions = {
    shadowUrl: 'images/marker-shadow.png',
    iconSize: [25, 41] as L.PointExpression,
    iconAnchor: [12, 41] as L.PointExpression,
    popupAnchor: [1, -38] as L.PointExpression,
    tooltipAnchor: [16, -28] as L.PointExpression,
    shadowSize: [41, 41] as L.PointExpression
  }
  currentLocationMarkerIcon = L.icon({
    ...this.sharedIconOptions,
    iconUrl: 'images/marker-blue-icon.png'
  });
  lastDayLocationMarkerIcon = L.icon({
    ...this.sharedIconOptions,
    iconUrl: 'images/marker-light-blue-icon.png'
  });
  lastMonthLocationMarkerIcon = L.icon({
    ...this.sharedIconOptions,
    iconUrl: 'images/marker-lighter-blue-icon.png'
  });
  panicMarkerIcon = L.icon({
    ...this.sharedIconOptions,
    iconUrl: 'images/marker-red-icon.png'
  });
  panicTestMarkerIcon = L.icon({
    ...this.sharedIconOptions,
    iconUrl: 'images/marker-orange-icon.png'
  });

  constructor(private dashboardData: MobileAppDashboardService, private toastHelper: ToastHelper, private dateTimeValueConverter: DateTimeValueConverter, private dialogHelper: DialogHelper, private mutex: TaskMutex) {
  }

  activate(params) {
    this.dashboardData.initializeService((userUniqueId: string, userName: string) => this.showUserLocation(userUniqueId, userName), (panicRequest: IPanicRequestDTO) => this.showPanicRequest(panicRequest));
    this.panicRequestId = params?.panicrequestid;
  }

  attached() {
    this.createMap();
    if (this.panicRequestId) {
      const panicRequest = this.dashboardData.panicRequests.panicRequests.find(x => x.id == this.panicRequestId);
      if (panicRequest)
        this.showPanicRequest(panicRequest);
      else
        this.toastHelper.toast("Panic request has not been found.", "Panic request not found", ToastType.Warning, ToastFormat.Html);
    }
  }

  deactivate() {
    this.dashboardData.stopService();
  }

  createMap() {
    this.map = L.map(this.locationsMapPlaceholder, {} as L.MapOptions).setView([38.272689, 10.810547], 3);
    L.esri.Vector.vectorTileLayer(
      "https://basemaps.arcgis.com/arcgis/rest/services/World_Basemap_v2/VectorTileServer",
      {
        apiKey: esriApiKey,
        token: null,
        portalUrl: null,
        style: null
      }
    ).addTo(this.map);
    this.map.setMaxZoom(this.mapMaxZoom);
    this.map.setMinZoom(this.mapMinZoom);
    this.userLocationsMarkers = L.markerClusterGroup({ disableClusteringAtZoom: this.map.getMaxZoom() } as L.MarkerClusterGroupOptions);
    this.panicRequestsMarkers = L.markerClusterGroup({ disableClusteringAtZoom: this.map.getMaxZoom() } as L.MarkerClusterGroupOptions);
    //this.updateMapData();
    this.map.addLayer(this.userLocationsMarkers);
    this.map.addLayer(this.panicRequestsMarkers);
    this.addControl(this.map, this.eventsList, 'topleft');
    this.addControl(this.map, this.installationsBox, 'topleft');
    this.addControl(this.map, this.activeUsersBox, 'topleft');
    this.addControl(this.map, this.trackingUsersBox, 'topleft');
    this.addControl(this.map, this.usersLocationsBox, 'topleft');
    this.addControl(this.map, this.panicAlertsBox, 'topleft');
    this.addControl(this.map, this.filterBox, 'topleft');
    this.addControl(this.map, this.locationsChartBox, 'bottomleft');
  }

  async filterMutated() {
    if (this.filter.user) {
      await this.showUserLocation(this.filter.user.uniqueid, this.filter.user.name);
      this.filter.user = null;
    }
  }

  public async showUserLocation(userUniqueId: string, userName: string) : Promise<void> {
    const filteredMarker = this.markersMap.get(userUniqueId.toLowerCase());
    if (filteredMarker) {
      if (this.map.getZoom() < this.getFilterZoomLevel())
        this.map.setView({ lat: filteredMarker.data.location.coordinates[0], lng: filteredMarker.data.location.coordinates[1] } as L.LatLngLiteral, this.getFilterZoomLevel());
      this.userLocationsMarkers.zoomToShowLayer(filteredMarker, () => { filteredMarker.openPopup(); });
    }
    else {
      if (await this.dialogHelper.confirm({ dialogTitle: "User's location not found", message: `<p>${userName}'s latest location has not been found.</p><p>Use the below button to request the user's location. The user will be notified of your request and can provide their location or refuse the request.</p>`, okTitle: 'Request location!', cancelTitle: 'Cancel' })) {
        await this.requestUserLocation(userUniqueId, userName);
      }
    }
  }

  async showPanicRequest(panicRequest: IPanicRequestDTO) : Promise<void> {
    this.panicRequestsMarkers.removeLayers(this.panicRequestsMarkers.getLayers());
    if (panicRequest.location && panicRequest.location.latitude && panicRequest.location.longitude) {
      const marker = new DataMarker(new L.LatLng(panicRequest.location.latitude, panicRequest.location.longitude), panicRequest, { title: panicRequest.user.fullName ? panicRequest.user.fullName : panicRequest.user.loginName, icon: panicRequest.isTest ? this.panicTestMarkerIcon : this.panicMarkerIcon, zIndexOffset: 1000 });
      marker.bindPopup(this.getPanicRequestMarkerPopup(panicRequest));
      this.panicRequestsMarkers.addLayer(marker);
      this.map.setView({ lat: panicRequest.location.latitude, lng: panicRequest.location.longitude } as L.LatLngLiteral, this.getFilterZoomLevel());
      marker.openPopup();
    }
    else {
      if (await this.dialogHelper.confirm({ dialogTitle: "Panic Request", message: `<div><label>Name:</label> ${panicRequest.user.fullName}</div><div><label>Login Name:</label> ${panicRequest.user.loginName}</div><div><label>Email:</label> ${panicRequest.user.email ? panicRequest.user.email : ''}</div><div><label>Phone number:</label> ${panicRequest.user.phoneNumber ? panicRequest.user.phoneNumber : ''}</div><div><label>Other phone numbers:</label> ${panicRequest.user.otherPhoneNumbers ? panicRequest.user.otherPhoneNumbers.join(", ") : ''}</div><div><label>Requested on:</label> ${this.dateTimeValueConverter.toView(panicRequest.timeStamp)}</div><div><label>Can speak:</label> ${panicRequest.cannotSpeak ? 'no' : 'yes'}</div><div><label>Location:</label><div style='margin-left: 5px;'>Panic Request location is not available.</div><div style='margin-left: 5px;'>Use the below button to show user's latest location</div></div>`, okTitle: 'Show latest location!', cancelTitle: 'Cancel' }))
        await this.showUserLocation(panicRequest.user.uniqueId, panicRequest.user.fullName);
    }
  }

  @watch((x: AdminMobileAppDashboardScreen) => x.dashboardData.usersLatestLocations)
  updateMapData() {
    this.loadData();
  }

  private addControl(map: L.Map, element: HTMLElement, position: L.ControlPosition) {
    const customControl = new (L.Control.extend({
      element: undefined,
      onAdd() {
        return this.element;
      },
      setElement(el) {
        this.element = el;
      }
    }))();
    customControl.setPosition(position)
    customControl.setElement(element);
    DomEvent.disableClickPropagation(element);
    DomEvent.disableScrollPropagation(element);
    this.map.addControl(customControl);
  }

  private loadData() {
    const removeMarkers = this.userLocationsMarkers.getLayers().map(x => x as DataMarker<IUserLocationDTO>).filter(x => !this.dashboardData.usersLatestLocations.some(d => d.user.uniqueId.toLowerCase() == x.data.user.uniqueId.toLowerCase()));
    removeMarkers.forEach(x => this.markersMap.delete(x.data.user.uniqueId.toLowerCase()));
    this.userLocationsMarkers.removeLayers(removeMarkers);
    this.dashboardData.usersLatestLocations.forEach(userLocation => {
      const marker = this.markersMap.get(userLocation.user.uniqueId.toLowerCase());
      if (marker) {
        if (marker.data.location.id !== userLocation.location.id) {
          marker.setLatLng(new L.LatLng(userLocation.location.coordinates[0], userLocation.location.coordinates[1]));
          marker.data = userLocation;
          this.bindUserLocationMarkerPopup(marker);
          this.setUserLocationMarker(marker);
        }
      }
    });
    const newMarkers = this.dashboardData.usersLatestLocations.filter(x => !this.userLocationsMarkers.getLayers().map(x => x as DataMarker<IUserLocationDTO>).some(m => m.data.user.uniqueId.toLowerCase() === x.user.uniqueId.toLowerCase()))
      .map(userLocation => {
        const marker = new DataMarker(new L.LatLng(userLocation.location.coordinates[0], userLocation.location.coordinates[1]), userLocation, { title: userLocation.user.fullName ? userLocation.user.fullName : userLocation.user.loginName });
        this.bindUserLocationMarkerPopup(marker);
        this.setUserLocationMarker(marker);
        return marker;
      });
    newMarkers.forEach(x => this.markersMap.set(x.data.user.uniqueId.toLowerCase(), x));
    this.userLocationsMarkers.addLayers(newMarkers, true);
  }

  private bindUserLocationMarkerPopup(marker: DataMarker<IUserLocationDTO>) {
    marker.bindPopup(this.getUserLocationMarkerPopup(marker.data))
      .on("popupopen", (a) => {
        var popUp = a.target.getPopup();
        var requestButton = popUp.getElement().querySelector(".locationRequest");
        requestButton.addEventListener("click", e => {
          this.mutex.executeIfNotRunning(() => {
            return this.requestUserLocation(requestButton.attributes.getNamedItem("data-userUniqueId").value, requestButton.attributes.getNamedItem("data-userFullName").value);
          });
        });
      });
  }

  private setUserLocationMarker(marker: DataMarker<IUserLocationDTO>) {
    marker.options.icon = this.getUserLocationMarkerIcon(marker.data);
  }

  private getUserLocationMarkerIcon(userLocation: IUserLocationDTO): L.Icon {
    switch (userLocation.location.currentnessStatus) {
      case LocationCurrentnessStatus.OneHour:
        return this.currentLocationMarkerIcon;
      case LocationCurrentnessStatus.OneDay:
        return this.lastDayLocationMarkerIcon;
      default:
        return this.lastMonthLocationMarkerIcon;
    }
  }

  private getUserLocationMarkerPopup(userLocation: IUserLocationDTO): string {
    return `<div><label>Name:</label> ${userLocation.user.fullName}</div><div><label>Email:</label> ${userLocation.user.email ? userLocation.user.email : ''}</div><div><label>Tracked on:</label> ${this.dateTimeValueConverter.toView(userLocation.location.timeStamp)}</div><div class="d-flex justify-content-center mt-2"><button type="button" class="locationRequest" data-userUniqueId="${userLocation.user.uniqueId}" data-userFullName="${userLocation.user.fullName}">Request current location</button></div>`;
  }

  private getPanicRequestMarkerPopup(panicRequest: IPanicRequestDTO): string {
    return `<div><label>Name:</label> ${panicRequest.user.fullName}</div><div><label>Login Name:</label> ${panicRequest.user.loginName}</div><div><label>Email:</label> ${panicRequest.user.email ? panicRequest.user.email : ''}</div><div><label>Phone number:</label> ${panicRequest.user.phoneNumber ? panicRequest.user.phoneNumber : ''}</div><div><label>Other phone numbers:</label> ${panicRequest.user.otherPhoneNumbers ? panicRequest.user.otherPhoneNumbers.join(", ") : ''}</div><div><label>Requested on:</label> ${this.dateTimeValueConverter.toView(panicRequest.timeStamp)}</div><div><label>Can speak:</label> ${panicRequest.cannotSpeak ? 'no' : 'yes'}</div><div><label>Location</label><div style='margin-left: 5px;'><label>Latitude:</label> ${panicRequest.location.latitude}</div><div style='margin-left: 5px;'><label>Longitude:</label> ${panicRequest.location.longitude}</div></div>`;
  }

  private getFilterZoomLevel() {
    return this.map.getMaxZoom() - this.mapFilterMaxZoomDelta;
  }

  private async requestUserLocation(userUniqueId: string, userName: string) {
    const locationRequest = await this.dashboardData.requestUserLocation(userUniqueId, userName);
    if (locationRequest.state.value === UserLocationRequestStatus.NotAvailable)
      this.toastHelper.toast(`${userName} does not actively use TravelSafe mobile app or has an old version which does not support location request.`, 'Location request not available', ToastType.Warning, ToastFormat.Html);
    else
      this.toastHelper.toast(`${userName}'s location request has been send.`, 'Location request has been send', ToastType.Success, ToastFormat.Html);
  }
}
